import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  MessageMessageTypeEnum,
  MessageSystemEventTypeEnum
} from '@/types/referral-outbound-center/models/Message';
import type { ReadReceipt } from '@/types/referral-outbound-center/models/ReadReceipt';
import { RecipientStatus as RecipientStatusEnum } from '@/types/referral-outbound-center/models/RecipientStatus';
import { ReferralDetailStatusEnum } from '@/types/referral-outbound-center/models/ReferralDetail';

dayjs.extend(utc);

export type MessageType = MessageMessageTypeEnum;
export type SystemEventType = MessageSystemEventTypeEnum;
export type RecipientStatus = RecipientStatusEnum;
export type ReferralStatus = ReferralDetailStatusEnum;

export interface ROCMessage {
  message_id: string;
  referral_id: string;
  message_type: MessageType;
  sender_user_id: string | null;
  sender_user_name?: string | null;
  sender_user_title?: string | null;
  sender_facility_id: string;
  receiver_facility_id?: string;
  content: string;
  system_event_type: SystemEventType | null;
  system_event_data: Record<string, unknown> | null;
  created_at: string;
}

export interface ROCRecipient {
  referral_id: string;
  organization_id: string;
  facility_id: string;
  facility_name?: string | null;
  status: RecipientStatus;
  created_at: string;
  updated_at: string;
}

export type ReferralDateType = 'manual' | 'start_of_care' | 'end_of_care';

export interface ROCReferral {
  referral_id: string;
  lead_id: string;
  sender_facility_id: string;
  sender_organization_id: string;
  target_discharge_date: string | null;
  // Absent on referrals created before this field existed — treat as manual.
  date_type?: ReferralDateType;
  referral_type: string;
  status: ReferralStatus;
  first_name: string | null;
  last_name: string | null;
  sent_at: string | null;
  confirmed_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ROCReferralThreadView
  extends Pick<
    ROCReferral,
    | 'referral_id'
    | 'sender_facility_id'
    | 'target_discharge_date'
    | 'date_type'
    | 'status'
    | 'first_name'
    | 'last_name'
  > {
  recipients: ROCRecipient[];
}

export interface ROCReferralDetail extends ROCReferral {
  recipients: ROCRecipient[];
}

export interface MessagesResponse {
  items: ROCMessage[];
  cursor: string | null;
  read_receipts: ReadReceipt[];
}

export interface DocumentAddedEventData {
  document_id: string;
  file_name: string;
  file_size_bytes: number;
  s3_key: string;
}

export class ReferralMessageModel implements ROCMessage {
  message_id: string;
  referral_id: string;
  message_type: MessageType;
  sender_user_id: string | null;
  sender_user_name?: string | null;
  sender_user_title?: string | null;
  sender_facility_id: string;
  receiver_facility_id?: string;
  content: string;
  system_event_type: SystemEventType | null;
  system_event_data: Record<string, unknown> | null;
  created_at: string;

  constructor(data: ROCMessage) {
    this.message_id = data.message_id;
    this.referral_id = data.referral_id;
    this.message_type = data.message_type;
    this.sender_user_id = data.sender_user_id;
    this.sender_user_name = data.sender_user_name;
    this.sender_user_title = data.sender_user_title;
    this.sender_facility_id = data.sender_facility_id;
    this.receiver_facility_id = data.receiver_facility_id;
    this.content = data.content;
    this.system_event_type = data.system_event_type;
    this.system_event_data = data.system_event_data;
    this.created_at = data.created_at;
  }

  isFromSender(senderFacilityId: string): boolean {
    return this.sender_facility_id === senderFacilityId;
  }

  formattedTime(): string {
    return dayjs.utc(this.created_at).local().format('h:mm A');
  }

  formattedDateTime(): string {
    return dayjs.utc(this.created_at).local().format('MMM D, YYYY, h:mm A');
  }

  isNewDay(prev: ReferralMessageModel | null): boolean {
    if (!prev) {
      return true;
    }

    return !dayjs
      .utc(this.created_at)
      .local()
      .isSame(dayjs.utc(prev.created_at).local(), 'day');
  }

  isSystemEvent(): boolean {
    return this.message_type === MessageMessageTypeEnum.System;
  }

  getDocumentAddedData(): DocumentAddedEventData | null {
    const d = this.system_event_data;
    if (!d) {
      return null;
    }
    const documentId = d.document_id;
    const fileName = d.file_name;
    const s3Key = d.s3_key;
    const fileSizeBytes = d.file_size_bytes;
    if (
      typeof documentId !== 'string' ||
      !documentId ||
      typeof fileName !== 'string' ||
      typeof s3Key !== 'string' ||
      !s3Key ||
      typeof fileSizeBytes !== 'number' ||
      !Number.isFinite(fileSizeBytes) ||
      fileSizeBytes < 0
    ) {
      return null;
    }
    return {
      document_id: documentId,
      file_name: fileName,
      file_size_bytes: fileSizeBytes,
      s3_key: s3Key
    };
  }

  static fromArray(items: ROCMessage[]): ReferralMessageModel[] {
    return items.map((item) => new ReferralMessageModel(item));
  }
}
